/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */


import APIBaseService from "./api.base.service";
import {exchangeTypeLookup} from "./exchange.service";

import {TTLCache} from '@brokerloop/ttlcache';
import {TickerService} from "./ticker.service";
import BigNumber from "bignumber.js";

const cache = new TTLCache<string, any>({
    ttl:   1000 * 60 * 60 * 24,
    max:   10,
    clock: Date
});

let supportsSockets: any = {
    'binanceus': true,
    'coinbasepro': true,
    'kraken': true,
    'gemini': true
}


let tickerSVC = new TickerService()
const url = "/v1/trader"
class TraderService extends APIBaseService{
    constructor() {
        super();
        this.usePathPrefix(url)
        tickerSVC.withTraderService(this)
    }

    async listAssets(exchangeID: any) {
        let result = await this.request(
            this.get(`/${exchangeID}/list-assets`, ),
            200
        )

        return result.data
    }

    cleanup(){
        tickerSVC.cleanup()
    }

    async listSymbols(exchangeID: any) {
        let key = `listSymbols.${exchangeID}`
        if(cache.has(key)){
            return cache.get(key)
        }
        let result = await this.request(
            this.get(`/${exchangeID}/list-symbols`),
            200
        )

        cache.set(key, result.data)

        return result.data
    }

    async tickers(exchangeID: any, symbols: any, bypass = false) {
        let exType: any = exchangeTypeLookup[exchangeID]
        if(supportsSockets[exType] && !bypass){

                return new Promise((resolve, reject) =>{
                    let loaded = false;
                    let cacheKey: any = exchangeID + JSON.stringify(symbols);
                    let timeoutID: any;

                    if(bypass ==false) {
                        timeoutID = setTimeout(()=>{
                            const cacheData = tickerSVC.parse(localStorage.getItem(cacheKey) as string);
                            if(!cacheData){
                                return
                            }

                            loaded = true
                            resolve(cacheData)

                        }, 500)
                    }

                    tickerSVC.tickersFor(exchangeID, symbols).then((data)=>{
                        if(!(timeoutID && !loaded)){return data}

                        localStorage.setItem(cacheKey, tickerSVC.stringify(data));
                        clearTimeout(timeoutID)
                        loaded = true
                        resolve(data)
                    }).catch((e)=>{
                        if(!loaded){
                            reject(e)
                        }
                    })
                })
        }

        let result = await this.request(
            this.post(`${exchangeID}/tickers`, {
                symbols: symbols,
            }),
            200
        )

        let results: Record<string, Record<any, BigNumber>> = {}
        Object.entries<Record<any, any>>(result.data).forEach(([key,val])=>{
            let data: Record<any, BigNumber> = {}
            Object.entries<any>(val).forEach(([key,val])=>{
                data[key] = new BigNumber(val)
            })
            results[key] = data
        })

        return results
    }

    async candles(exchangeID: any, symbols: any) {
        let result = await this.request(
            this.post(`/${exchangeID}/candles`, {
                symbols: symbols,
            }),
            200
        )

        return result.data
    }

    async order(exchangeID: any, order: any) {
        let result = await this.request(
            this.post(`/${exchangeID}/order/`, {
                order: order,
            }),
            200
        )

        return result.data
    }

    async cancelOrder(exchangeID: any, orderID: any) {
        let result = await this.request(
            this.delete(`/${exchangeID}/order/${orderID}`),
            204
        )

        return result.data
    }

    async orderInfo(exchangeID: any, orderID: any) {
        let result = await this.request(
            this.get(`/${exchangeID}/order/${orderID}`),
            204
        )

        return result.data
    }

    async orderTrades(exchangeID: any, orderID: any) {
        let result = await this.request(
            this.get(`/${exchangeID}/order/${orderID}/trades`),
            200
        )

        return result.data
    }
}

export {TraderService}
